import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { AssetLine, POStatus, poTotals, useStore } from "../../store/store";
import {
  numberToWords,
  generateSampleCSV,
  parseCSVToAssets,
} from "../../lib/currency-utils";
import { formatCurrencyINR, getCompactINR } from "../../lib/enhanced-currency-utils";
import { EnhancedTable, Column } from "../../components/ui/enhanced-table";
import * as XLSX from "xlsx";
import {
  BulkUpdateDialog,
  BulkExportDialog,
} from "../../components/ui/bulk-operations";
import { validatePOData, validateCSVFormat } from "../../lib/validation";
import { validateFileType } from "../../lib/file-storage";
import {
  Download,
  Upload,
  Trash2,
  Copy,
  Plus,
  Settings,
  HandCoins,
  Wallet,
  Boxes,
  DollarSign,
  Package,
  CheckCircle,
  Eye,
  Loader2,
  Sparkles,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { GSTSettingsDialog } from "../../components/purchaseOrder/GSTSettingsDialog";
import { MagicUploadDialog, MagicUploadRow } from "../../components/purchaseOrder/MagicUploadDialog";
import {
  GSTTreatment,
  determineGSTTreatmentFromStateId,
  applyGSTRules,
  calculateGSTBreakdown,
} from "../../lib/gst-rules";
import apiService from "../../services/apiService";
import { Address, Supplier } from "../../types/supplier";
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import DropdownFilter from "../../components/DropdownFilter";
import { CustomerAddressManagementDialog } from "../../components/customers/CustomerAddressManagementDialog";
import { fetchCustomerAddresses, getAddressTypeLabel } from "../../lib/customer-addresses";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";

interface ApiResponse<T> {
  data: T;
  downloaded: T;
  message?: string;
  status?: number;
}

export interface PurchaseOrder {
  id: number;
  customerId: string;
  supplierId: string;
  status: POStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deliveryAddress?: string;
  supplierAddress?: string;
  supplierGST?: string;
  productType?: string;
  approvals?: {
    underwriter: boolean;
    comments?: string;
  };
  attachments?: string[];
  lines: AssetLine[];
  purchaseOrderCode?: string;
  customerGST?:string;
  paidAmount?:string;
  isRentalScheduleTagged?:boolean;
}

interface PurchaseOrderProps {
  handleViewPos: (po: any,) => void;
  indiaState: any[]
  customers?: any;
}



export default function PurchaseOrders({ handleViewPos, indiaState, customers }: PurchaseOrderProps) {

  const { customerId } = useParams();
  const { invoices, updatePO } =
    useStore();

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[] | []>([]);
  const [totalPurchaseOrders, setTotalPurchaseOrders] = useState<PurchaseOrder[] | []>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [params] = useSearchParams();
  const filter = params.get("filter");
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  const [selectedPOs, setSelectedPOs] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[] | []>([]);
  const [deliveryAddress, setDeliveryAddress] = useState<any[]>([]);
  const [dashboardSummary, setDashboardSummary] = useState<any>();
  const [tablePageSize, setTablePageSize] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [poPendingDelete, setPoPendingDelete] = useState<PurchaseOrder | null>(null);
  const [isDeletingPo, setIsDeletingPo] = useState(false);
  const [poPendingDuplicate, setPoPendingDuplicate] = useState<PurchaseOrder | null>(null);
  const [isDuplicatingPo, setIsDuplicatingPo] = useState(false);

  useEffect(() => {
    fetchPO();
    fetchSuppliers();
    fetchCustomerAddress();
    fetchSummary();
  }, []);

  const createPO = async () => {
    fetchPO();
    fetchSuppliers();
  }


  const fetchSummary = async () => {
    const response = await apiService.get<any>(`/purchase-order/get-dashboard-summary/${customerId}`);
    setDashboardSummary(response.data);
  };

  const fetchSuppliers = async () => {
    const response = await apiService.get<any>(`/supplier/getSupplierslist`);
    setSuppliers(response.suppliers);
  };

  const fetchPO = async (page: number = 1) => {
    try {
      setLoading(true);
      const response = await apiService.post<any>(
        `/purchase-order/list/${customerId}`,
        {
          search: undefined,
          status: filter === "review" ? "review" : undefined,
          pagination: {
            pageSize: tablePageSize,
            currentPage: page,
          },
        }
      );

      setPurchaseOrders(response.data || []);
      setTotalPurchaseOrders(response.allCustomerPurchaseOrders || []);
      setTablePageSize(response?.pagination?.pageSize || 25);
      setCurrentPage(response?.pagination?.currentPage || page);
      setTotalRecords(response?.pagination?.totalRecords || response?.data?.length || 0);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      toast.error("Failed to fetch purchase orders");
      setPurchaseOrders([]);
      setTotalPurchaseOrders([]);
      setTablePageSize(25);
      setCurrentPage(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerAddress = async () => {
    if (!customerId) {
      setDeliveryAddress([]);
      return;
    }
    try {
      const list = await fetchCustomerAddresses(customerId);
      setDeliveryAddress(list);
    } catch {
      setDeliveryAddress([]);
    }
  };

  const confirmDeletePurchaseOrder = async () => {
    if (!poPendingDelete) return;
    if (Boolean(poPendingDelete.isRentalScheduleTagged)) {
      toast.error("Cannot delete: this PO is linked to a rental schedule");
      setPoPendingDelete(null);
      return;
    }
    try {
      setIsDeletingPo(true);
      await apiService.post(`/purchase-order/delete-po/${poPendingDelete.id}`, {});
      toast.success("Purchase order deleted");
      setPoPendingDelete(null);
      await fetchPO(currentPage);
      await fetchSummary();
    } catch (error) {
      console.error("Failed to delete purchase order:", error);
      toast.error("Failed to delete purchase order");
    } finally {
      setIsDeletingPo(false);
    }
  };

  const confirmDuplicatePurchaseOrder = async () => {
    if (!poPendingDuplicate?.id) {
      toast.error("Purchase order ID not found");
      return;
    }

    try {
      setIsDuplicatingPo(true);
      await apiService.post(`/purchase-order/duplicate-po/${poPendingDuplicate.id}`, {});
      toast.success("Purchase order duplicated successfully");
      setPoPendingDuplicate(null);
      await fetchPO(currentPage);
      await fetchSummary();
    } catch (error) {
      console.error("Failed to duplicate purchase order:", error);
      toast.error("Failed to duplicate purchase order");
    } finally {
      setIsDuplicatingPo(false);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "review")
      return totalPurchaseOrders.filter((p) => p.status === "review");
    return totalPurchaseOrders;
  }, [totalPurchaseOrders, filter]);

  const isRentalScheduleTaggedPo = (po: PurchaseOrder) =>
    Boolean(po.isRentalScheduleTagged);

  // Enhanced table columns
  const columns: Column<any>[] = [
    {
      key: "purchaseOrderCode",
      header: "PO ID",
      sortable: true,
      filterable: true,
      width: "w-32",
      render: (value) => (
        <span className="font-mono text-sm font-medium">{value}</span>
      ),
    },
    {
      key: "productType",
      header: "Product Type",
      sortable: true,
      render: (productType: string) => (
        <div className="text-sm text-muted-foreground">
          {productType}
        </div>
      ),
    },
    {
      key: "approvedAt",
      header: "Approved At",
      sortable: true,
      render: (date, po) => (
        <div className="text-sm text-muted-foreground">
          {po.status === "approved" || po.status === "delivered"
            ? new Date(po.updatedAt || date).toLocaleDateString("en-IN")
            : "—"}
        </div>
      ),
    },
    {
      key: "supplierId",
      header: "Supplier Name",
      sortable: true,
      filterable: true,
      render: (supplierId) => {
        const supplier = suppliers.find((s) => String(s.id) === String(supplierId));
        return (
          <div className="font-medium">{supplier?.company_name || "—"}</div>
        );
      },
    },
    {
      key: "assets",
      header: "Total Quantity",
      sortable: true,
      render: (_value,po) => {
        const totalQty =
          po?.lines?.reduce((sum: number, line: AssetLine) => sum + (Number(line.qty) || 0), 0) || 0;
        const rounded = Math.round(totalQty * 100) / 100;
        const displayQty = Number.isInteger(rounded)
          ? String(rounded)
          : rounded.toFixed(2);
        return (
          <div className="text-center">
            {displayQty}
          </div>
        );
      },
    },
    {
      key: "lines",
      header: "Amount",
      sortable: true,
      render: (lines) => {
        const totals = poTotals(lines);
        return (
          <div className="text-left">
            <div className="font-medium">{formatCurrencyINR(totals.total)}</div>
          </div>
        );
      },
    },
    {
      key: "paid",
      header: "Paid",
      sortable: true,
      render: (_value,po) => {
        return (
          <div className="text-left">
            <span className={Number(po?.paidAmount) > 0 ? "text-[hsl(var(--status-paid))] font-semibold" : "text-muted-foreground"}>
                    {formatCurrencyINR(po?.paidAmount || 0)}
                  </span>
          </div>
        );
      },
    },
    {
      key: "delivered",
      header: "Delivered",
      sortable: true,
      render: (lines: any[]) => {
        const deliveredCount =
          lines?.filter((line: any) => line.status === "delivered").length || 0;
        return <div className="text-center font-medium">{deliveredCount}</div>;
      },
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      filterable: true,
      filterType: "select",
      filterOptions: [
        { label: "Draft", value: "draft" },
        { label: "Review", value: "review" },
        { label: "Approved", value: "approved" },
        { label: "Delivered", value: "delivered" },
      ],
      render: (status: string) => <Badge className={getStatusBadge(status)} >{status.toUpperCase()}</Badge>,
    },
    {
      key: "actions",
      header: "Action",
      render: (_, po) => {
        const tagged = isRentalScheduleTaggedPo(po);
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              title="View"
              onClick={() => handleViewPos(po)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0 text-[#3f8b98] hover:bg-[#3f8b98]/10 hover:text-[#357a85]"
              title="Duplicate purchase order"
              aria-label="Duplicate purchase order"
              onClick={() => setPoPendingDuplicate(po)}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={tagged}
              className={
                tagged
                  ? "h-8 w-8 shrink-0 cursor-not-allowed opacity-40 text-muted-foreground hover:bg-transparent hover:text-muted-foreground"
                  : "h-8 w-8 shrink-0 text-destructive hover:text-destructive hover:bg-red-50"
              }
              title={
                tagged
                  ? "Cannot delete: this PO is linked to a rental schedule"
                  : "Delete"
              }
              onClick={() => {
                if (tagged) return;
                setPoPendingDelete(po);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Bulk actions
  const bulkActions = [
    {
      label: "Update Status",
      action: () => setBulkUpdateOpen(true),
    },
    {
      label: "Export Selected",
      action: () => setBulkExportOpen(true),
    },
    {
      label: "Delete Selected",
      variant: "destructive" as const,
      action: (items: any[]) => {
        // Handle bulk delete
      },
    },
  ];

  const handleBulkUpdate = (updateData: any) => {
    updateData.items.forEach((po: any) => {
      updatePO(po.id, updateData.updates);
    });
  };

  const handleExport = (data: any[]) => {
    // Convert to CSV format
    const headers = [
      "PO ID",
      "Customer",
      "Supplier",
      "Status",
      "Amount",
      "Created Date",
    ];
    const rows = data.map((po) => {
      const customer = customers?.find((c: any) => c.id === po.customerId);
      const supplier = suppliers.find((s) => s.id === po.supplierId);
      const totals = poTotals(po.lines);
      return [
        po.id,
        customer?.name || "",
        supplier?.company_name || "",
        po.status,
        totals.total,
        new Date(po.createdAt).toLocaleDateString("en-IN"),
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `purchase_orders_${new Date().toISOString().split("T")[0]
      }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const normalizeReportRows = (rows: unknown[]): Record<string, unknown>[] => {
    return rows.map((row) => {
      if (typeof row !== "object" || row === null) {
        return { Value: row };
      }
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
        const header = key
          .replace(/_/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        out[header] =
          value !== null && typeof value === "object" ? JSON.stringify(value) : value ?? "";
      }
      return out;
    });
  };

  const extractReportPayload = (response: any) => {
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) {
      return { reportRows: payload, summaryRows: null as unknown[] | null };
    }

    const reportRows =
      payload?.assetLines ??
      payload?.assetLineReport ??
      payload?.report ??
      payload?.lines ??
      payload?.purchaseOrders ??
      (Array.isArray(payload?.data) ? payload.data : []);

    const summaryRows = payload?.summary ?? null;
    return {
      reportRows: Array.isArray(reportRows) ? reportRows : [],
      summaryRows: Array.isArray(summaryRows) ? summaryRows : null,
    };
  };

  const mapSummaryRow = (row: Record<string, unknown>) => ({
    "Supplier Name": String(
      row.supplierName ??
        row.supplier_name ??
        row["Supplier Name"] ??
        row["Supplier name"] ??
        "—"
    ),
    "PO ID": String(
      row.purchaseOrderCode ??
        row.purchaseOrderId ??
        row.poId ??
        row.po_id ??
        row["PO ID"] ??
        row["Po Id"] ??
        ""
    ),
    Amount: Number(row.amount ?? row.total ?? row.Amount ?? row["Amount"] ?? 0),
  });

  const handleDownloadPreview = async (selectedPOs: PurchaseOrder[]) => {
    if (selectedPOs.length === 0) return;

    const purchaseOrderIds = selectedPOs.map((po) => po.id);

    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-po-invoice-asset-line-report`,
        {
          params: {
            customerId,
            purchaseOrderIds,
          },
        }
      );

      const { reportRows, summaryRows: apiSummaryRows } = extractReportPayload(response);

      if (!reportRows.length && !(apiSummaryRows?.length)) {
        toast.error("No report data returned for the selected purchase orders");
        return;
      }

      const excelData = normalizeReportRows(reportRows);

      let summarySheetRows: Array<{
        "Supplier Name": string;
        "PO ID": string;
        Amount: number;
      }>;

      if (apiSummaryRows?.length) {
        summarySheetRows = normalizeReportRows(apiSummaryRows).map(mapSummaryRow);
      } else {
        summarySheetRows = selectedPOs.map((po) => {
          const supplier = suppliers.find((s) => String(s.id) === String(po.supplierId));
          const { total: amount } = poTotals(po.lines || []);
          return {
            "Supplier Name": supplier?.company_name || "—",
            "PO ID": po.purchaseOrderCode || String(po.id),
            Amount: amount,
          };
        });
      }

      const summaryTotal = summarySheetRows.reduce((sum, row) => sum + row.Amount, 0);
      summarySheetRows.push({
        "Supplier Name": "TOTAL",
        "PO ID": "",
        Amount: summaryTotal,
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summarySheetRows), "Summary");

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(excelData.length ? excelData : [{ Message: "No line data" }]),
        "Asset Line Report"
      );

      const fileName = `purchase_orders_preview_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(`Downloaded report for ${selectedPOs.length} purchase order(s)`);
    } catch (error) {
      console.error("Failed to download PO preview report:", error);
      toast.error("Failed to download preview report");
      throw error;
    }
  };

  const getPoIdFromList = (po: PurchaseOrder) => String(po.id);

  const handleDownloadPreviewPdf = async (poIds: string[]) => {
    if (poIds.length === 0) return;

    const purchaseOrderIds = poIds.map((id) => Number(id));

    try {
      const blob = await apiService.get<Blob>(
        `/purchase-order/download-po-preview-pdf`,
        {
          params: {
            customerId,
            purchaseOrderIds,
          },
          responseType: "blob",
        }
      );

      const fileName = `purchase_orders_preview_${new Date().toISOString().split("T")[0]}.zip`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded PDF zip for ${poIds.length} purchase order(s)`);
    } catch (error) {
      console.error("Failed to download PO preview PDF:", error);
      toast.error("Failed to download preview PDF");
      throw error;
    }
  };

  return (
    <>
      {/* <SEO
        title="Purchase Orders • Admin Portal"
        description="Create, review, approve and track POs. Export PDFs and manage deliveries."
        canonical="/pos"
      /> */}
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Purchase Orders</h1>
          {/* <p className="text-muted-foreground">
            Admin dashboard for purchase order management and tracking.
          </p> */}
        </div>
        <CreatePOSheet
          suppliers={suppliers}
          onCreate={createPO}
          deliveryAddress={deliveryAddress}
          indiaState={indiaState}
          customers={customers}
        />
      </header>

      <div className="space-y-6">
        {/* Dashboard Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
          <Card className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Sanction Limit
                </p>
                <p className="text-2xl font-bold text-primary">
                  {getCompactINR(dashboardSummary?.totalSanctioned || 0)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-primary/60" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Utilized Limit
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {getCompactINR(dashboardSummary?.totalDisbursed || 0)}
                </p>
              </div>
              <HandCoins className="h-8 w-8 text-green-500/60" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Available Amount
                </p>
                <p className="text-2xl font-bold text-amber-600">
                  {getCompactINR(dashboardSummary?.outstanding || 0)}
                </p>
              </div>
              <Wallet className="h-8 w-8 text-amber-500/60" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-teal-500/10 to-teal-500/5 border-teal-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Asset Value
                </p>
                <p className="text-2xl font-bold text-teal-600">
                  {getCompactINR(dashboardSummary?.totalAssetValue || 0)}
                </p>
              </div>
              <Boxes className="h-8 w-8 text-teal-500/60" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Invoice Raised
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {getCompactINR(dashboardSummary?.totalInvoiceRow || 0)}
                </p>
              </div>
              <Package className="h-8 w-8 text-blue-500/60" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Invoice Paid
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  {getCompactINR(dashboardSummary?.totalPayments || 0)}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-purple-500/60" />
            </div>
          </Card>
        </div>

        <EnhancedTable
          data={filtered}
          columns={columns}
          title={`Purchase Orders (${totalRecords})`}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          pagination={true}
          pageSize={tablePageSize}
          serverPagination={true}
          currentPage={currentPage}
          totalRecords={totalRecords}
          onPageChange={fetchPO}
          onSelectionChange={setSelectedPOs}
          bulkActions={bulkActions}
          onExport={handleExport}
          onDownloadPreview={(rows) =>
            handleDownloadPreview(rows as unknown as PurchaseOrder[])
          }
          getRowId={(po) => getPoIdFromList(po as PurchaseOrder)}
          onDownloadPreviewPdf={(poIds) => handleDownloadPreviewPdf(poIds)}
          onRefresh={() => window.location.reload()}
          customActions={
            <Button variant="outline" size="sm" asChild>
              <Link to="/pos?filter=review">
                Review Queue (
                {totalPurchaseOrders.filter((p) => p.status === "review").length})
              </Link>
            </Button>
          }
        />
      </div>

      <BulkUpdateDialog
        open={bulkUpdateOpen}
        onOpenChange={setBulkUpdateOpen}
        selectedItems={selectedPOs}
        onUpdate={handleBulkUpdate}
        entityType="po"
      />

      <BulkExportDialog
        open={bulkExportOpen}
        onOpenChange={setBulkExportOpen}
        data={selectedPOs}
        entityType="Purchase Orders"
      />

      <AlertDialog
        open={!!poPendingDelete}
        onOpenChange={(open) => {
          if (!open && !isDeletingPo) setPoPendingDelete(null);
        }}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this purchase order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the PO
              {poPendingDelete?.purchaseOrderCode != null && poPendingDelete.purchaseOrderCode !== "" ? (
                <span className="mt-1 block font-mono text-xs text-slate-700">
                  {poPendingDelete.purchaseOrderCode}
                </span>
              ) : (
                <span className="mt-1 block font-mono text-xs text-slate-700">ID {poPendingDelete?.id}</span>
              )}
              from the list. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingPo}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="border-transparent bg-red-600 text-white hover:bg-red-700 hover:text-white focus-visible:ring-red-600"
              disabled={isDeletingPo}
              onClick={() => void confirmDeletePurchaseOrder()}
            >
              {isDeletingPo ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!poPendingDuplicate}
        onOpenChange={(open) => {
          if (!open && !isDuplicatingPo) setPoPendingDuplicate(null);
        }}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate this purchase order?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  Are you sure you want to create a duplicate of this Purchase Order?
                </p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <p>
                    <span className="font-semibold text-slate-800">PO Code:</span>{" "}
                    {poPendingDuplicate?.purchaseOrderCode || "-"}
                  </p>
                  <p className="mt-1">
                    <span className="font-semibold text-slate-800">PO ID:</span>{" "}
                    {poPendingDuplicate?.id ?? "-"}
                  </p>
                </div>
                <p>
                  A new Purchase Order will be created in Draft status with a newly
                  generated PO Code.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDuplicatingPo}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              className="border-transparent bg-[#3f8b98] text-white hover:bg-[#357a85] hover:text-white"
              disabled={isDuplicatingPo}
              onClick={() => void confirmDuplicatePurchaseOrder()}
            >
              {isDuplicatingPo ? "Duplicating…" : "Confirm"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


const getStatusBadge = (status: string) => {
  const variants: Record<string, string> = {
    pending: "bg-[hsl(var(--status-pending))]/10 text-[hsl(var(--status-pending))] border-[hsl(var(--status-pending))]/20 hover:bg-[hsl(var(--status-pending))]/20 hover:text-[hsl(var(--status-pending))] hover:border-[hsl(var(--status-pending))]/20",
    approved: "bg-[hsl(var(--status-approved))]/10 text-[hsl(var(--status-approved))] border-[hsl(var(--status-approved))]/20 hover:bg-[hsl(var(--status-approved))]/10 hover:text-[hsl(var(--status-approved))] hover:border-[hsl(var(--status-approved))]/20",
    completed: "bg-[hsl(var(--status-delivered))]/10 text-[hsl(var(--status-delivered))] border-[hsl(var(--status-delivered))]/20 hover:bg-[hsl(var(--status-delivered))]/10 hover:text-[hsl(var(--status-delivered))] hover:border-[hsl(var(--status-delivered))]/20",
    delivered: "bg-[hsl(var(--status-delivered))]/10 text-[hsl(var(--status-delivered))] border-[hsl(var(--status-delivered))]/20 hover:bg-[hsl(var(--status-delivered))]/10 hover:text-[hsl(var(--status-delivered))] hover:border-[hsl(var(--status-delivered))]/20",
    partial: "bg-[hsl(var(--brand-gold))]/10 text-[hsl(var(--brand-gold))] border-[hsl(var(--brand-gold))]/20 hover:bg-[hsl(var(--brand-gold))]/10 hover:text-[hsl(var(--brand-gold))] hover:border-[hsl(var(--brand-gold))]/20",
  };
  return variants[status.toLowerCase()] || variants.pending;
};

const CUSTOMER_REQUIREMENT_OPTIONS = [
  { value: "asset_leasing", label: "Asset Leasing" },
  { value: "sale_lease_back", label: "Sale and Lease Back" },
] as const;
type CustomerRequirementType = (typeof CUSTOMER_REQUIREMENT_OPTIONS)[number]["value"] | "";

/** When API returns only type "Leasing", show a clearer label (productTypeName preferred) */
function requirementTypeDisplayLabel(req: { productTypeName?: string; type?: string }): string {
  if (req.productTypeName) return req.productTypeName;
  if (req.type === "Leasing") return "Asset Leasing";
  if (req.type === "Hybrid") return "Hybrid";
  if (req.type === "Smart EPP") return "Smart EPP";
  return req.type || "Requirement";
}

function CreatePOSheet({
  suppliers,
  customers,
  onCreate,
  deliveryAddress,
  indiaState
}: any) {
  // const { refreshWorkflow } = useCustomerWorkflow();
  const { workflow } = useCustomerWorkflow();
  const { customerId } = useParams();
  const [open, setOpen] = useState(false);
  const [gstSettingsOpen, setGstSettingsOpen] = useState(false);
  const [magicUploadOpen, setMagicUploadOpen] = useState(false);
  const [csvUploadDialogOpen, setCsvUploadDialogOpen] = useState(false);
  const [customerRequirementType, setCustomerRequirementType] = useState<CustomerRequirementType>("");
  const [requirements, setRequirements] = useState<{ id: string; requirementId: string; type?: string; productTypeName?: string }[]>([]);
  const [selectedRequirementId, setSelectedRequirementId] = useState<string>("");
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [fetchedDeliveryAddress, setFetchedDeliveryAddress] = useState<any[] | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  // Sale and Lease Back: customer as supplier
  const [saleLeaseBackAddressIndex, setSaleLeaseBackAddressIndex] = useState<number | null>(null);
  const [saleLeaseBackContactPerson, setSaleLeaseBackContactPerson] = useState(customers?.customerName || "");
  const [saleLeaseBackCompanyName, setSaleLeaseBackCompanyName] = useState(customers?.entityName || "");
  const [saleLeaseBackPan, setSaleLeaseBackPan] = useState(customers?.pan || "");
  const [isSaleLeaseBackPanFromApi, setIsSaleLeaseBackPanFromApi] = useState(false);
  const [saleLeaseBackContactMobile, setSaleLeaseBackContactMobile] = useState(customers?.customerPhoneNumber || "");
  const [saleLeaseBackContactEmail, setSaleLeaseBackContactEmail] = useState(customers?.customerEmail || "");
  const [gstTreatment, setGstTreatment] = useState<GSTTreatment>("cgst_sgst");

  type AssetLine = {
    item: string;
    description: string;
    hsn: string;
    unitPrice: number;
    qty: number;
    /** Draft string while typing decimals (e.g. "0." / "0.05") */
    qtyStr?: string;
    taxRate: number;
    assetType?: string;
    cgst?: number;
    sgst?: number;
    igst?: number;
    utgst?: number;
  };

  const getDefaultAssetLine = (): AssetLine => {
    const breakdown = calculateGSTBreakdown(18, gstTreatment);
    return {
      item: "",
      description: "",
      hsn: "",
      unitPrice: 0,
      qty: 1,
      taxRate: 18,
      assetType: "it-equipment",
      ...breakdown,
    };
  };

  const [selectedGstinIndex, setSelectedGstinIndex] = useState(0);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [addDeliveryAddressModalOpen, setAddDeliveryAddressModalOpen] = useState(false);
  const [viewAllDeliveryAddressesModalOpen, setViewAllDeliveryAddressesModalOpen] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);
  

  // ✅ State for selected supplier
  const [selectedSupplier, setSelectedSupplier] = useState<any>(
    null
  );

  // ✅ Controlled selected SPOC id (string)
  const [selectedSpocId, setSelectedSpocId] = useState<string>("");

  // ✅ Derived data states (addresses + gstins)
  const [supplierGstins, setSupplierGstins] = useState<string[]>([]);
  const [supplierAddresses, setSupplierAddresses] = useState<any[]>([]);

  const handleRequirementChange = (reqId: string) => {
    const requirement = requirements.find((r) => String(r.id) === String(reqId));

    setSelectedRequirementId(String(requirement?.id));
    const isSaleLeaseBack = requirement?.productTypeName?.toLowerCase().includes("sale and leaseback");
    setCustomerRequirementType(isSaleLeaseBack ? "sale_lease_back" : "asset_leasing");

    // If Sale and Lease Back: check if customer is already a supplier and prefill SPOC data
    if (isSaleLeaseBack && customerId) {
      apiService
        .get<any>(`/supplier/get-supplier-by-customer/${customerId}`)
        .then((response) => {
          const supplier = response?.data ?? response;
          if (supplier?.id) {
            setSaleLeaseBackCompanyName(supplier.companyName ?? customers?.entityName ?? "");
            const panFromApi = (supplier.pan ?? customers?.pan ?? "")?.toString?.()?.trim?.() ?? "";
            setSaleLeaseBackPan(supplier.pan ?? customers?.pan ?? "");
            setIsSaleLeaseBackPanFromApi(Boolean(panFromApi));
            const spoc = supplier.spocDetails?.find((s: any) => Number(s.isPrimary) === 1) ?? supplier.spocDetails?.[0];
            if (spoc) {
              setSaleLeaseBackContactPerson(spoc.name ?? customers?.customerName ?? "");
              setSaleLeaseBackContactMobile(spoc.mobile ?? customers?.customerPhoneNumber ?? "");
              setSaleLeaseBackContactEmail(spoc.email ?? customers?.customerEmail ?? "");
            } else {
              setSaleLeaseBackContactPerson(customers?.customerName ?? "");
              setSaleLeaseBackContactMobile(customers?.customerPhoneNumber ?? "");
              setSaleLeaseBackContactEmail(customers?.customerEmail ?? "");
            }
          }
        })
        .catch(() => {
          // Customer not a supplier: keep existing customer defaults
          setSaleLeaseBackContactPerson(customers?.customerName ?? "");
          setSaleLeaseBackCompanyName(customers?.entityName ?? "");
          const panFromApi = (customers?.pan ?? "")?.toString?.()?.trim?.() ?? "";
          setSaleLeaseBackPan(customers?.pan ?? "");
          setIsSaleLeaseBackPanFromApi(Boolean(panFromApi));
          setSaleLeaseBackContactMobile(customers?.customerPhoneNumber ?? "");
          setSaleLeaseBackContactEmail(customers?.customerEmail ?? "");
        });
    }
  };

 

  // Fetch requirements when sheet opens and customerId is available
  useEffect(() => {
    if (!open || !customerId) return;
    let cancelled = false;
    setLoadingRequirements(true);
    apiService
      .get<any>(`/login/get-requirements/${customerId}`)
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response) ? response : response?.data ?? response?.requirements ?? [];
        setRequirements(
          list.map((r: any) => ({
            id: String(r.id ?? r.requirementId ?? ""),
            requirementId: r.requirementId ?? r.id ?? "",
            type: r.type ?? "",
            productTypeName: r.productTypeName ?? r.productType ?? "",
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setRequirements([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRequirements(false);
      });
    return () => { cancelled = true; };
  }, [open, customerId]);

  // Fetch delivery addresses when sheet opens and customerId is available
  useEffect(() => {
    if (!open || !customerId) return;
    let cancelled = false;
    setLoadingAddresses(true);
    fetchCustomerAddresses(customerId)
      .then((list) => {
        if (!cancelled) setFetchedDeliveryAddress(list);
      })
      .catch(() => {
        if (!cancelled) setFetchedDeliveryAddress([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAddresses(false);
      });
    return () => { cancelled = true; };
  }, [open, customerId]);

  // Use fetched addresses when sheet is open, otherwise fall back to prop
  const deliveryAddressList = fetchedDeliveryAddress !== null ? fetchedDeliveryAddress : deliveryAddress;
 

  // Clear selection if selected requirement is not type "Leasing" (dropdown shows only Leasing)
  useEffect(() => {
    if (!selectedRequirementId) return;
    const selected = requirements.find((r) => String(r.id) === String(selectedRequirementId));
    if (selected && selected.type !== "Leasing") {
      setSelectedRequirementId("");
    }
  }, [requirements, selectedRequirementId]);

  // ✅ Update supplier whenever supplierId or suppliers change
  useEffect(() => {
    if (suppliers?.length) {
      const supplier = suppliers.find(
        (s:any) => String(s.id) === String(supplierId)
      );
      setSelectedSupplier(supplier || null);
    }
  }, [supplierId, suppliers]);

  useEffect(() => {
    if (selectedSupplier?.spocDetails?.length) {
      const primary = selectedSupplier.spocDetails.find(
        (s: any) => Number(s.isPrimary) === 1
      );
      const id = primary ? primary.id : selectedSupplier.spocDetails[0].id;
      setSelectedSpocId(String(id));
    } else {
      setSelectedSpocId("");
    }
  }, [selectedSupplier]);

  // helper to get currently selected spoc object
  const currentSpoc = selectedSupplier?.spocDetails?.find(
      (s: any) => String(s.id) === String(selectedSpocId)
    ) || selectedSupplier?.spocDetails?.[0] || null;

  // ✅ When selectedSupplier changes, recalculate GSTINs & addresses
  useEffect(() => {
    if (selectedSupplier) {
      const addresses = selectedSupplier.addresses || [];
      const gstins =
        addresses.map((addr: any) => addr.gstin).filter(Boolean) || [];

      setSupplierAddresses(addresses);
      setSupplierGstins(gstins);
      // setDeliveryAddressOptions(deliveryOptions);

      // Optional: reset address selectors
      setSelectedGstinIndex(0);
      setSelectedAddressIndex(0);
      // setSelectedDeliveryAddressIndex(0);
    }
  }, [selectedSupplier]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  const handleChange = (value: string) => {
    const index = Number(value); // convert string to number
    const address = deliveryAddressList[index];
    setSelectedIndex(index);
    setSelectedAddress(address);
  };

  // Resolve state to ID: if value is numeric or matches an id use it; else find by state_name (for supplier addresses that may use name).
  const resolveStateId = (stateValue: string | number | undefined, states: any[]): string | number | undefined => {
    if (stateValue == null || stateValue === "" || !states?.length) return undefined;
    const str = String(stateValue).trim();
    const byId = states.find((s: any) => String(s.id) === str);
    if (byId) return byId.id;
    const byName = states.find((s: any) => String(s.state_name || "").toLowerCase() === str.toLowerCase());
    return byName ? byName.id : undefined;
  };

  // Resolve state id or name → always return state_name for PO payload / display.
  // Newly added addresses often store state as id; existing API addresses may already have the name.
  const resolveStateName = (address: any, states: any[] = indiaState ?? []): string => {
    const raw = address?.state_id ?? address?.state;
    if (raw == null || raw === "") return "";
    const str = String(raw).trim();
    const byId = states.find((s: any) => String(s.id) === str);
    if (byId?.state_name) return String(byId.state_name);
    const byName = states.find(
      (s: any) => String(s.state_name || "").toLowerCase() === str.toLowerCase()
    );
    if (byName?.state_name) return String(byName.state_name);
    return str;
  };

  const formatCustomerDeliveryAddress = (address: any): string => {
    if (!address) return "";
    const stateName = resolveStateName(address);
    return `${address.address_line1}, ${address.address_line2 || ""}, ${address.city}, ${stateName} - ${address.pincode}`
      .replace(/,\s*,/g, ",")
      .trim();
  };

  // Auto-set GST treatment when supplier or address/GST changes. Works for both sale_lease_back and asset_leasing. Our state Karnataka (id 12) vs supplier state ID.
  useEffect(() => {
    const isSaleLeaseBack = customerRequirementType === "sale_lease_back";
    const rawState = isSaleLeaseBack
      ? (saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]?.state)
      : (supplierAddresses[selectedGstinIndex]?.state);
    const supplierStateId = resolveStateId(rawState, indiaState ?? []);

    if (!supplierStateId || !indiaState?.length) return;

    const supplierGstin = isSaleLeaseBack
      ? (saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]?.gstin) || ""
      : (supplierGstins[selectedGstinIndex] ?? "");

    const treatment = determineGSTTreatmentFromStateId(supplierStateId, indiaState, 12);
    setGstTreatment(treatment);
    setLines((prev) =>
      prev.map((line) =>
        applyGSTRules(line, supplierGstin, treatment)
      )
    );
  }, [
    customerRequirementType,
    selectedGstinIndex,
    supplierAddresses,
    supplierGstins,
    supplierId,
    saleLeaseBackAddressIndex,
    deliveryAddressList,
    indiaState,
  ]);



  const [attachment, setAttachment] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<{
    supplier?: string;
    gstin?: string;
    deliveryAddress?: string;
    pan?: string;
    saleLeaseBackContact?: string;
    saleLeaseBackMobile?: string;
    saleLeaseBackEmail?: string;
    lines?: Record<number, { item?: string; unitPrice?: string; qty?: string }>;
  }>({});

  const isValidPan = (value: string): boolean => /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/.test((value || "").trim());

  const [linesImportKey, setLinesImportKey] = useState(0);
  const [lines, setLines] = useState<AssetLine[]>(() => [getDefaultAssetLine()]);

  const resetCreatePOForm = () => {
    setFieldErrors({});
    setValidationErrors([]);
    setCustomerRequirementType("");
    setSelectedRequirementId("");
    setFetchedDeliveryAddress(null);
    setSupplierId("");
    setSelectedIndex(null);
    setSaleLeaseBackAddressIndex(null);
    setSaleLeaseBackContactPerson(customers?.customerName ?? "");
    setSaleLeaseBackCompanyName(customers?.entityName ?? "");
    const panOnClose = (customers?.pan ?? "")?.toString?.()?.trim?.() ?? "";
    setSaleLeaseBackPan(customers?.pan ?? "");
    setIsSaleLeaseBackPanFromApi(Boolean(panOnClose));
    setSaleLeaseBackContactMobile(customers?.customerPhoneNumber ?? "");
    setSaleLeaseBackContactEmail(customers?.customerEmail ?? "");
    setLines([getDefaultAssetLine()]);
    setLinesImportKey((k) => k + 1);
    setAttachment(null);
    setCsvUploadDialogOpen(false);
  };
  

  const addLine = () => {
    const breakdown = calculateGSTBreakdown(18, gstTreatment);
    setLines((l) => [
      ...l,
      {
        item: "",
        description: "",
        hsn: "",
        unitPrice: 0,
        qty: 1,
        taxRate: 18,
        assetType: "it-equipment",
        ...breakdown,
      },
    ]);
  };

  const removeLine = (index: number) => {
    if (lines.length > 1) {
      setLines((l) => l.filter((_, i) => i !== index));
    }
  };


  const downloadSampleCSV = () => {
    const csv = generateSampleCSV(gstTreatment);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_assets.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleMagicUploadData = (rows: MagicUploadRow[]) => {
    const supplierGstin =
      customerRequirementType === "sale_lease_back"
        ? (saleLeaseBackAddressIndex !== null &&
            deliveryAddressList[saleLeaseBackAddressIndex]?.gstin) ||
          ""
        : supplierGstins[selectedGstinIndex] || "";

    // Match document tax type so extracted % are not remapped / double-counted
    const hasIgst = rows.some((r) => Number(r.igst) > 0);
    const hasIntra = rows.some(
      (r) => Number(r.cgst) > 0 || Number(r.sgst) > 0 || Number(r.utgst) > 0
    );
    const treatment: GSTTreatment =
      hasIgst && !hasIntra
        ? "igst"
        : hasIntra && rows.some((r) => Number(r.utgst) > 0)
          ? "cgst_utgst"
          : hasIntra
            ? "cgst_sgst"
            : gstTreatment;

    if (treatment !== gstTreatment) {
      setGstTreatment(treatment);
    }

    const mappedLines = rows
      .map((row) => {
        const cgst = Number(row.cgst) || 0;
        const sgst = Number(row.sgst) || 0;
        const igst = Number(row.igst) || 0;
        const utgst = Number(row.utgst) || 0;
        // One rate only — do not add IGST on top of CGST/SGST
        const taxRate = igst > 0 ? igst : cgst + sgst + utgst;

        const line: AssetLine = {
          item: row.itemName || "",
          description: row.description || "",
          hsn: row.hsnCode || "",
          unitPrice: Number(row.unitPrice) || 0,
          qty: Number(row.quantity) || 1,
          cgst,
          sgst,
          igst,
          utgst,
          taxRate,
          assetType: "it-equipment",
        };

        return applyGSTRules(line, supplierGstin, treatment) as AssetLine;
      })
      .filter(
        (line) =>
          line.item?.trim() ||
          line.description?.trim() ||
          line.hsn?.trim() ||
          Number(line.unitPrice) > 0
      );

    if (mappedLines.length === 0) {
      toast.error("No valid asset rows found in extracted data");
      return;
    }

    flushSync(() => {
      setLines([]);
    });
    setLines(mappedLines);
    setLinesImportKey((k) => k + 1);
    toast.success(`${mappedLines.length} assets imported from document`);
    setMagicUploadOpen(false);
  };

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const input = event.target;
    if (!file) return;

    if (!validateFileType(file, ["text/csv", "application/vnd.ms-excel"])) {
      toast("Please upload a CSV file");
      input.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target?.result as string;
      const validation = validateCSVFormat(csvContent);

      if (!validation.valid) {
        toast(`Invalid CSV format: ${validation.errors.join("; ")}`);
        input.value = "";
        return;
      }

      const supplierGstin =
        customerRequirementType === "sale_lease_back"
          ? (saleLeaseBackAddressIndex !== null &&
              deliveryAddressList[saleLeaseBackAddressIndex]?.gstin) ||
            ""
          : supplierGstins[selectedGstinIndex] || "";

      const parsedAssets = parseCSVToAssets(csvContent).filter(
        (asset) =>
          asset.item?.trim() ||
          asset.description?.trim() ||
          asset.hsn?.trim() ||
          Number(asset.unitPrice) > 0
      );

      if (parsedAssets.length === 0) {
        toast("No asset rows found in CSV");
        input.value = "";
        return;
      }

      const processedAssets = parsedAssets.map((asset) =>
        applyGSTRules(asset, supplierGstin, gstTreatment)
      ) as AssetLine[];

      // Clear existing rows before applying CSV so stale fields are not kept
      flushSync(() => {
        setLines([]);
      });
      setLines(processedAssets);
      setLinesImportKey((k) => k + 1);
      toast(`${processedAssets.length} assets imported with GST rules applied`);
      setCsvUploadDialogOpen(false);
      input.value = "";
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    setLines((prev) =>
      prev.map((l) => {
        const taxRate =
          Number(l.taxRate) ||
          (Number(l.igst) || 0) ||
          (Number(l.cgst) || 0) + (Number(l.sgst) || 0) + (Number(l.utgst) || 0);
        return applyGSTRules({ ...l, taxRate }, "", gstTreatment) as AssetLine;
      })
    );
  }, [gstTreatment]);
  


  // Validate fields and return field-level errors
  const validateFields = () => {
    const errors: {
      supplier?: string;
      gstin?: string;
      deliveryAddress?: string;
      pan?: string;
      saleLeaseBackContact?: string;
      saleLeaseBackMobile?: string;
      saleLeaseBackEmail?: string;
      lines?: Record<number, { item?: string; unitPrice?: string; qty?: string }>;
    } = {};

    // Customer requirement type required
    if (!customerRequirementType) {
      errors.supplier = 'Please select Customer Requirement type (Asset Leasing or Sale and Lease Back)';
    }

    // Validate supplier / sale and lease back
    if (customerRequirementType === "sale_lease_back") {
      if (saleLeaseBackAddressIndex === null || !deliveryAddressList[saleLeaseBackAddressIndex]) {
        errors.gstin = 'Please select a GSTIN';
      }
      if (!saleLeaseBackContactPerson?.trim()) {
        errors.saleLeaseBackContact = 'Contact Person is required';
      }
      if (!saleLeaseBackPan?.trim()) {
        errors.pan = 'PAN is required';
      } else if (!isValidPan(saleLeaseBackPan)) {
        errors.pan = 'Invalid PAN format (e.g. ABCDE1234F)';
      }
      if (!saleLeaseBackContactMobile?.trim()) {
        errors.saleLeaseBackMobile = 'Mobile Number is required';
      }
      if (!saleLeaseBackContactEmail?.trim()) {
        errors.saleLeaseBackEmail = 'Email ID is required';
      }
    } else if (customerRequirementType === "asset_leasing") {
      if (!supplierId || supplierId.trim() === '') {
        errors.supplier = 'Supplier is required';
      }
    }

    // Validate delivery address
    if (selectedIndex === null || !deliveryAddressList[selectedIndex]) {
      errors.deliveryAddress = 'Delivery address is required';
    }

    // Validate lines
    const lineErrors: Record<number, { item?: string; unitPrice?: string; qty?: string }> = {};
    lines.forEach((line, index) => {
      const lineError: { item?: string; unitPrice?: string; qty?: string } = {};
      
      if (!line.item?.trim()) {
        lineError.item = 'Item name is required';
      }
      
      if (!line.unitPrice || line.unitPrice <= 0) {
        lineError.unitPrice = 'Unit price must be greater than 0';
      }
      
      if (!line.qty || line.qty <= 0) {
        lineError.qty = 'Quantity must be greater than 0';
      }
      
      if (Object.keys(lineError).length > 0) {
        lineErrors[index] = lineError;
      }
    });

    if (Object.keys(lineErrors).length > 0) {
      errors.lines = lineErrors;
    }

    return errors;
  };

  const create = async () => {
    if (isCreating) return;
    // Clear previous errors
    setFieldErrors({});
    setValidationErrors([]);

    // Validate fields
    const fieldValidationErrors = validateFields();
    if (Object.keys(fieldValidationErrors).length > 0) {
      setFieldErrors(fieldValidationErrors);
      const errorMessages: string[] = [];
      if (fieldValidationErrors.supplier) errorMessages.push(fieldValidationErrors.supplier);
      if (fieldValidationErrors.pan) errorMessages.push(fieldValidationErrors.pan);
      if (fieldValidationErrors.deliveryAddress) errorMessages.push(fieldValidationErrors.deliveryAddress);
      if (fieldValidationErrors.lines) {
        Object.entries(fieldValidationErrors.lines).forEach(([idx, errs]) => {
          Object.values(errs).forEach(msg => {
            if (msg) errorMessages.push(`Line ${Number(idx) + 1}: ${msg}`);
          });
        });
      }
      setValidationErrors(errorMessages);
      toast.error("Please fix the validation errors before creating the PO");
      return;
    }

    const isSaleLeaseBack = customerRequirementType === "sale_lease_back";
    const effectiveSupplierId = isSaleLeaseBack ? (customerId ?? "") : supplierId;
    const effectiveSupplierAddress = isSaleLeaseBack
      ? saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]
        ? formatCustomerDeliveryAddress(deliveryAddressList[saleLeaseBackAddressIndex])
        : ""
      : supplierAddresses[selectedGstinIndex]
        ? `${supplierAddresses[selectedGstinIndex].address_line1}, ${supplierAddresses[selectedGstinIndex].address_line2 || ""}, ${supplierAddresses[selectedGstinIndex].city}, ${resolveStateName(supplierAddresses[selectedGstinIndex])} - ${supplierAddresses[selectedGstinIndex].pincode}`
        : "";
    const effectiveSupplierGST = isSaleLeaseBack
      ? (saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]?.gstin) || ""
      : supplierAddresses[selectedGstinIndex]?.gstin ?? "";

    const selectedRequirement = requirements.find((r) => String(r.id) === String(selectedRequirementId));

    // Validate PO data
    let poData: any = {
      customerId,
      supplierId: effectiveSupplierId,
      requirementId: (selectedRequirementId) || undefined,
      requirementDbId: selectedRequirementId || undefined,
      requirementType: selectedRequirement?.type,
      isSaleLeaseBack: !!isSaleLeaseBack,
      deliveryAddress: formatCustomerDeliveryAddress(deliveryAddressList[selectedIndex!]),
      customerGST: deliveryAddressList[selectedIndex!]?.gstin,
      supplierAddress: effectiveSupplierAddress,
      supplierGST: effectiveSupplierGST,
      supplierSpocId: isSaleLeaseBack ? undefined : selectedSpocId,
      ...(isSaleLeaseBack && {
        saleLeaseBackCompanyName:saleLeaseBackCompanyName,
        saleLeaseBackContactPerson: saleLeaseBackContactPerson.trim(),
        saleLeaseBackPan: saleLeaseBackPan.trim(),
        saleLeaseBackContactMobile: saleLeaseBackContactMobile.trim(),
        saleLeaseBackContactEmail: saleLeaseBackContactEmail.trim(),
      }),
      lines: lines.map((l) => ({
        id: Math.random().toString(36).slice(2),
        item: l.item,
        description: l.description,
        hsn: l.hsn,
        unitPrice: l.unitPrice,
        qty: l.qty,
        assetType: l.assetType,
        cgst: l.cgst,
        sgst: l.sgst,
        igst: l.igst,
        utgst: l.utgst,
        taxRate:
          gstTreatment === "igst"
            ? Number(l.igst) || 0
            : (Number(l.cgst) || 0) +
              (Number(l.sgst) || 0) +
              (Number(l.utgst) || 0),
      })),
      attachmentUrl: null,
    };

    const validation = validatePOData(poData);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      toast.error("Please fix the errors before creating the PO");
      return;
    }


    setIsCreating(true);
    try {
      if (attachment) {
      try {
        const formData = new FormData();
        // use field name expected by backend; here 'files' (adjust if API expects 'file')
        formData.append("files", attachment);
        formData.append("folder", customerId + '/po/attachment');
        // formData.append("folder", '/Tarun');
       
        const uploadResponse = await apiService.post<any>(
          `/purchase-order/doc-upload`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        // backend should return an array of URLs; adapt access path if different
        const urls = uploadResponse.data?.fileKeys ?? uploadResponse.data ?? [];
        poData.attachmentUrl = urls[0];

        try {
          const response = await apiService.post<ApiResponse<any>>(
            `/purchase-order/create-po`,
            poData
          );
          if (response.status == 200) {
            toast.success("Purchase Order Created Successfully")
          }
        } catch (err) {
          toast.error('Purchase Order Creation Failed')
        }
      } catch (error) {
        toast.error("Failed to upload attachment");
        return;
      }
    } else {
      try {
        const response = await apiService.post<ApiResponse<any>>(
          `/purchase-order/create-po`,
          poData
        );
        if (response.status == 200) {
          toast.success("Purchase Order Created Successfully")
        }
      } catch (err) {
        toast.error('Purchase Order Creation Failed')
      }
    }

    onCreate();
    resetCreatePOForm();
    setOpen(false);
    } finally {
      setIsCreating(false);
      // await refreshWorkflow();
    }
    // toast("Purchase order created successfully");
    // nav("/pos");
  };


  function handleSelectSuppliers(selected:any): void {
    // (selected: any) => setSupplierId(selected?.value?.toString() || '')
    setSupplierId(selected[selected.length -1].value || '')
  }

  const selectedSupplierCreatePo = suppliers.find(
  (s:Supplier) => String(s.id) === String(supplierId)
);


const handleUpdateAddresses = (addresses: any[]) => {
  setAddresses([...addresses]);
  setFetchedDeliveryAddress(addresses);
};

const handleViewAllDeliveryAddresses = (open: boolean) => {
  setViewAllDeliveryAddressesModalOpen(open);
  setAddDeliveryAddressModalOpen(open);
};

  return (
    <>
      <Sheet open={open} onOpenChange={(isOpen: any) => {
        setOpen(isOpen);
        if (isOpen) {
          setIsSaleLeaseBackPanFromApi(false);
        }
        if (!isOpen) {
          resetCreatePOForm();
        }
      }}>
        <SheetTrigger asChild>
          <Button variant="hero" disabled={!workflow.createPurchaseOrder.enabled} tooltip={workflow.createPurchaseOrder.reason} >Create PO</Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[95vh] max-h-[95vh] ms-auto w-[96%] rounded-t-xl">
          <div className="h-full flex flex-col">
            <SheetHeader className="p-6 border-b">
              <div className="flex items-center justify-between">
                <SheetTitle>New Purchase Order</SheetTitle>
              </div>
            </SheetHeader>

            <ScrollArea className="flex-1 p-6">
              <div className="space-y-6 max-w-6xl mx-auto">
                {/* Customer Requirement - single column, show type-requirementId */}
                <div className="space-y-4">
                  <div className="font-semibold text-lg">Customer Requirement</div>
                  <div className="grid grid-cols-1 gap-4 max-w-2xl">
                    <div className="space-y-2">
                      <Label>Requirement</Label>
                      <Select
                        value={selectedRequirementId}
                        onValueChange={(v: string) => handleRequirementChange(v)}
                        disabled={loadingRequirements}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={loadingRequirements ? "Loading..." : "Select requirement"} />
                        </SelectTrigger>
                        <SelectContent>
                          {requirements
                            .filter((req) => req.type === "Leasing")
                            .map((req) => (
                              <SelectItem key={req.id} value={req.id}>
                                {requirementTypeDisplayLabel(req) + " - " + (req.requirementId || req.id)}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Supplier Details Section - only when requirement type selected */}
                {customerRequirementType && (
                <div className="space-y-4">
                  <div className="font-semibold text-lg">
                    {customerRequirementType === "sale_lease_back" ? "Supplier Details (Customer as Supplier)" : "Supplier Details"}
                  </div>
                  {customerRequirementType === "sale_lease_back" ? (
                    /* Sale and Lease Back: Customer as Supplier - select by GSTIN */
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Customer as Supplier Name</Label>
                        <Input
                          value={saleLeaseBackCompanyName}
                          onChange={(e) => setSaleLeaseBackCompanyName(e.target.value)}
                          disabled
                          className="bg-muted"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>GSTIN *</Label>
                        <Select
                          value={saleLeaseBackAddressIndex !== null ? String(saleLeaseBackAddressIndex) : ""}
                          onValueChange={(v: string) => {
                            const idx = v === "" ? null : parseInt(v, 10);
                            setSaleLeaseBackAddressIndex(idx);
                            if (fieldErrors.gstin) setFieldErrors(prev => ({ ...prev, gstin: undefined }));
                          }}
                        >
                          <SelectTrigger className={fieldErrors.gstin ? "border-red-500 border-2" : ""}>
                            <SelectValue placeholder="Select GSTIN" />
                          </SelectTrigger>
                          <SelectContent>
                            {deliveryAddressList
                              ?.map((addr: any, index: number) => ({ addr, index }))
                              .filter(({ addr }: { addr: any }) => addr.gstin)
                              .map(({ addr, index }: { addr: any; index: number }) => (
                                <SelectItem key={addr.id ?? addr.gstin ?? index} value={String(index)}>
                                  {addr.gstin}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {fieldErrors.gstin && (
                          <p className="text-sm text-red-600 font-medium">{fieldErrors.gstin}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>PAN *</Label>
                        <Input
                          value={saleLeaseBackPan}
                          onChange={(e) => {
                            const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
                            setSaleLeaseBackPan(v);
                            if (fieldErrors.pan) setFieldErrors(prev => ({ ...prev, pan: undefined }));
                          }}
                          disabled={isSaleLeaseBackPanFromApi}
                          className={isSaleLeaseBackPanFromApi ? "bg-muted" : fieldErrors.pan ? "border-red-500 border-2" : ""}
                          placeholder="e.g. ABCDE1234F"
                          maxLength={10}
                        />
                        {fieldErrors.pan && (
                          <p className="text-sm text-red-600 font-medium">{fieldErrors.pan}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Person *</Label>
                        <Input
                          value={saleLeaseBackContactPerson}
                          onChange={(e) => {
                            setSaleLeaseBackContactPerson(e.target.value);
                            if (fieldErrors.saleLeaseBackContact) {
                              setFieldErrors(prev => ({ ...prev, saleLeaseBackContact: undefined }));
                            }
                          }}
                          className={fieldErrors.saleLeaseBackContact ? "border-red-500 border-2" : ""}
                          placeholder="Contact person name"
                        />
                        {fieldErrors.saleLeaseBackContact && (
                          <p className="text-sm text-red-600 font-medium">{fieldErrors.saleLeaseBackContact}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Mobile Number *</Label>
                        <Input
                          value={saleLeaseBackContactMobile}
                          onChange={(e) => {
                            setSaleLeaseBackContactMobile(e.target.value);
                            if (fieldErrors.saleLeaseBackMobile) {
                              setFieldErrors(prev => ({ ...prev, saleLeaseBackMobile: undefined }));
                            }
                          }}
                          className={fieldErrors.saleLeaseBackMobile ? "border-red-500 border-2" : ""}
                          placeholder="Mobile number"
                        />
                        {fieldErrors.saleLeaseBackMobile && (
                          <p className="text-sm text-red-600 font-medium">{fieldErrors.saleLeaseBackMobile}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Email ID *</Label>
                        <Input
                          type="email"
                          value={saleLeaseBackContactEmail}
                          onChange={(e) => {
                            setSaleLeaseBackContactEmail(e.target.value);
                            if (fieldErrors.saleLeaseBackEmail) {
                              setFieldErrors(prev => ({ ...prev, saleLeaseBackEmail: undefined }));
                            }
                          }}
                          className={fieldErrors.saleLeaseBackEmail ? "border-red-500 border-2" : ""}
                          placeholder="Email"
                        />
                        {fieldErrors.saleLeaseBackEmail && (
                          <p className="text-sm text-red-600 font-medium">{fieldErrors.saleLeaseBackEmail}</p>
                        )}
                      </div>
                      <div className="space-y-2 col-span-3">
                        <Label>Supplier Address</Label>
                        <Textarea
                          value={
                            saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]
                              ? `${deliveryAddressList[saleLeaseBackAddressIndex].address_line1}, ${deliveryAddressList[saleLeaseBackAddressIndex].address_line2 || ""}, ${deliveryAddressList[saleLeaseBackAddressIndex].city}, ${deliveryAddressList[saleLeaseBackAddressIndex].state}, ${deliveryAddressList[saleLeaseBackAddressIndex].pincode}`
                              : "—"
                          }
                          disabled
                          className="bg-muted min-h-[60px]"
                        />
                      </div>
                    </div>
                  ) : (
                    /* Asset Leasing: normal supplier selection */
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Supplier *</Label>
                        <DropdownFilter
                          options={suppliers
                            .filter((supplier: any) => supplier.is_customer == false)
                            .map((supplier: any) => ({
                              label: supplier.company_name || "Unknown Supplier",
                              value: supplier.id.toString(),
                            }))}
                          selectedOptions={
                            selectedSupplierCreatePo
                              ? [
                                  {
                                    label: selectedSupplierCreatePo.company_name || "Unknown Supplier",
                                    value: selectedSupplierCreatePo.id.toString(),
                                  },
                                ]
                              : []
                          }
                          onChange={(selected) => {
                            handleSelectSuppliers(selected);
                            if (fieldErrors.supplier) {
                              setFieldErrors(prev => ({ ...prev, supplier: undefined }));
                            }
                          }}
                          placeholder="Select Suppliers"
                          fullWidth={true}
                        />
                        {fieldErrors.supplier && (
                          <p className="text-sm text-red-600 font-medium">{fieldErrors.supplier}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Person</Label>
                        <select
                          className="w-full bg-muted border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={selectedSpocId}
                          onChange={(e) => setSelectedSpocId(e.target.value)}
                        >
                          {selectedSupplier?.spocDetails?.length ? (
                            selectedSupplier.spocDetails.map((spoc: any) => (
                              <option key={spoc.id} value={String(spoc.id)}>
                                {spoc.name}
                              </option>
                            ))
                          ) : (
                            <option value="">—</option>
                          )}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Mobile Number</Label>
                        <Input
                          value={currentSpoc?.mobile || "—"}
                          disabled
                          className="bg-muted"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          value={currentSpoc?.email || "—"}
                          disabled
                          className="bg-muted"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>GSTIN</Label>
                        {supplierGstins.length > 1 ? (
                          <Select
                            value={selectedGstinIndex.toString()}
                            onValueChange={(v: string) => setSelectedGstinIndex(Number(v))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {supplierGstins.map((gstin, index) => (
                                <SelectItem key={index} value={index.toString()}>
                                  {gstin}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={supplierGstins[0] || "—"}
                            disabled
                            className="bg-muted"
                          />
                        )}
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Supplier Address</Label>
                        <Textarea
                          value={
                            supplierAddresses[selectedGstinIndex]
                              ? `${supplierAddresses[selectedGstinIndex].address_line1}, ${supplierAddresses[selectedGstinIndex].address_line2 || ""}, ${supplierAddresses[selectedGstinIndex].city}, ${ supplierAddresses[selectedGstinIndex].state } - ${supplierAddresses[selectedGstinIndex].pincode}`
                              : "—"
                          }
                          disabled
                          className="bg-muted min-h-[60px]"
                        />
                      </div>
                    </div>
                  )}

                  {/* Delivery Address Dropdown - show GST number with address */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Delivery Address *</Label>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setViewAllDeliveryAddressesModalOpen(true)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View All Addresses
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setAddDeliveryAddressModalOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Address
                        </Button>
                      </div>
                    </div>
                    <Select
                      value={selectedIndex !== null ? selectedIndex.toString() : ""}
                      onValueChange={(value: string) => {
                        handleChange(value);
                        if (fieldErrors.deliveryAddress) {
                          setFieldErrors(prev => ({ ...prev, deliveryAddress: undefined }));
                        }
                      }}
                    >
                      <SelectTrigger className={`text-sm break-words whitespace-normal min-h-[2.5rem] ${fieldErrors.deliveryAddress ? "border-red-500 border-2" : ""}`}>
                        <SelectValue placeholder="Select address" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                        {deliveryAddressList?.length > 0 && deliveryAddressList?.map((address: any, index: number) => (
                          <SelectItem key={address.id ?? index} value={index.toString()} className="text-sm break-words whitespace-normal py-2">
                            <span className="text-sm break-words whitespace-normal block">
                              {`${getAddressTypeLabel(address.address_type)} — ${address.address_line1}, ${address.address_line2 || ""}, ${address.city}, ${indiaState.find((state: any) => String(state.id) === String(address.state_id || address.state))?.state_name}, ${address.pincode}`}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.deliveryAddress && (
                      <p className="text-sm text-red-600 font-medium">{fieldErrors.deliveryAddress}</p>
                    )}
                  </div>
                </div>
                )}

                <Separator />

                {/* Asset Details Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-lg">Asset Details</div>
                    <div className="flex gap-2">
                     
                      <Dialog open={csvUploadDialogOpen} onOpenChange={setCsvUploadDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Upload className="h-4 w-4 mr-2" />
                            Bulk Upload CSV
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Upload Asset CSV</DialogTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={downloadSampleCSV}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download Sample CSV
                            </Button>
                          </DialogHeader>
                          <div className="space-y-4">
                         
                            <div className="text-sm text-muted-foreground">
                              Upload a CSV file with asset details. Make sure
                              your CSV follows the sample format.
                            </div>
                            <Input
                              type="file"
                              accept=".csv"
                              onChange={handleCSVUpload}
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setGstSettingsOpen(true)}
                        className="group"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        GST Settings
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setMagicUploadOpen(true)}
                        className="bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] text-white border-0 shadow-md shadow-[hsl(var(--brand))]/25 hover:shadow-lg hover:shadow-[hsl(var(--brand))]/35 hover:opacity-95 transition-all"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Magic Upload
                      </Button>
                    </div>
                  </div>

                  {/* Asset Headers */}
                  <div className="grid grid-cols-12 gap-3 text-sm font-medium text-muted-foreground border-b pb-2">
                    <div className="col-span-3">Item Description *</div>
                    <div className="col-span-2">HSN Code</div>
                    <div className="col-span-2">Base Price (₹) *</div>
                    <div className="col-span-1">Qty *</div>
                    {gstTreatment === "igst" ? (
                      <div className="col-span-2">IGST %</div>
                    ) : gstTreatment === "cgst_utgst" ? (
                      <>
                        <div className="col-span-1">CGST %</div>
                        <div className="col-span-1">UTGST %</div>
                      </>
                    ) : (
                      <>
                        <div className="col-span-1">CGST %</div>
                        <div className="col-span-1">SGST %</div>
                      </>
                    )}
                    <div className="col-span-1 text-right">Total (₹)</div>
                    <div className="col-span-1 text-center">Action</div>
                  </div>

                  {/* Asset Rows */}
                  {lines.map((l, idx) => {
                    const taxAmount =
                      gstTreatment === "igst"
                        ? l.igst || 0
                        : (l.cgst || 0) + (l.sgst || 0) + (l.utgst || 0);
                    const total = l.unitPrice * l.qty * (1 + taxAmount / 100);
                    const amountInWords =
                      l.unitPrice > 0 ? numberToWords(l.unitPrice) : "";

                    return (
                      <div
                        key={`${linesImportKey}-${idx}`}
                        className="grid grid-cols-12 gap-3 py-3 border-b text-sm items-end"
                      >
                        {/* Item Name Input */}
                        <div className="col-span-3 space-y-2">
                          <Input
                            placeholder="Item name *"
                            value={l.item}
                            onChange={(e) => {
                              setLines((prev) =>
                                prev.map((line, i) =>
                                  i === idx
                                    ? { ...line, item: e.target.value }
                                    : line
                                )
                              );
                              if (fieldErrors.lines?.[idx]?.item) {
                                setFieldErrors(prev => ({
                                  ...prev,
                                  lines: {
                                    ...prev.lines,
                                    [idx]: { ...prev.lines?.[idx], item: undefined }
                                  }
                                }));
                              }
                            }}
                            className={`font-medium ${fieldErrors.lines?.[idx]?.item ? "border-red-500 border-2" : ""}`}
                          />
                          {fieldErrors.lines?.[idx]?.item && (
                            <p className="text-xs text-red-600 font-medium">{fieldErrors.lines[idx].item}</p>
                          )}
                          <Textarea
                            placeholder="Item description"
                            value={l.description}
                            onChange={(e) =>
                              setLines((prev) =>
                                prev.map((line, i) =>
                                  i === idx
                                    ? { ...line, description: e.target.value }
                                    : line
                                )
                              )
                            }
                            className="min-h-[60px] resize-none text-sm"
                          />
                          {amountInWords && (
                            <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                              <strong>Amount in words:</strong> {amountInWords}
                            </div>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Input
                            placeholder="HSN Code"
                            value={l.hsn}
                            onChange={(e) =>
                              setLines((prev) =>
                                prev.map((line, i) =>
                                  i === idx
                                    ? { ...line, hsn: e.target.value }
                                    : line
                                )
                              )
                            }
                          />
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Input
                            type="number"
                            placeholder="0"
                            value={l.unitPrice || ""}
                            onChange={(e) => {
                              setLines((prev) =>
                                prev.map((line, i) =>
                                  i === idx
                                    ? {
                                      ...line,
                                      unitPrice:
                                        parseFloat(e.target.value) || 0,
                                    }
                                    : line
                                )
                              );
                              if (fieldErrors.lines?.[idx]?.unitPrice) {
                                setFieldErrors(prev => ({
                                  ...prev,
                                  lines: {
                                    ...prev.lines,
                                    [idx]: { ...prev.lines?.[idx], unitPrice: undefined }
                                  }
                                }));
                              }
                            }}
                            className={fieldErrors.lines?.[idx]?.unitPrice ? "border-red-500 border-2" : ""}
                          />
                          {fieldErrors.lines?.[idx]?.unitPrice && (
                            <p className="text-xs text-red-600 font-medium">{fieldErrors.lines[idx].unitPrice}</p>
                          )}
                        </div>
                        <div className="col-span-1 space-y-2">
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="1"
                            value={
                              l.qtyStr !== undefined
                                ? l.qtyStr
                                : l.qty === 0
                                  ? ""
                                  : String(l.qty)
                            }
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              // Allow empty / intermediate decimals like ".", "0.", "0.05"
                              if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;

                              const parsed = parseFloat(raw);
                              setLines((prev) =>
                                prev.map((line, i) =>
                                  i === idx
                                    ? {
                                      ...line,
                                      qtyStr: raw,
                                      qty:
                                        raw === "" ||
                                        raw === "." ||
                                        Number.isNaN(parsed)
                                          ? 0
                                          : parsed,
                                    }
                                    : line
                                )
                              );
                              if (fieldErrors.lines?.[idx]?.qty) {
                                setFieldErrors(prev => ({
                                  ...prev,
                                  lines: {
                                    ...prev.lines,
                                    [idx]: { ...prev.lines?.[idx], qty: undefined }
                                  }
                                }));
                              }
                            }}
                            onBlur={() => {
                              setLines((prev) =>
                                prev.map((line, i) =>
                                  i === idx
                                    ? {
                                      ...line,
                                      qtyStr: undefined,
                                      qty: Number(line.qty) || 0,
                                    }
                                    : line
                                )
                              );
                            }}
                            className={fieldErrors.lines?.[idx]?.qty ? "border-red-500 border-2" : ""}
                          />
                          {fieldErrors.lines?.[idx]?.qty && (
                            <p className="text-xs text-red-600 font-medium">{fieldErrors.lines[idx].qty}</p>
                          )}
                        </div>

                        {/* Dynamic Tax Fields based on GST Treatment */}
                        {gstTreatment === "igst" ? (
                          <div className="col-span-2">
                            <Input
                              type="number"
                              placeholder="18"
                              value={l.igst || ""}
                              onChange={(e) =>
                                setLines((prev) =>
                                  prev.map((line, i) =>
                                    i === idx
                                      ? {
                                        ...line,
                                        igst: parseFloat(e.target.value) || 0,
                                        cgst: 0,
                                        sgst: 0,
                                        utgst: 0,
                                      }
                                      : line
                                  )
                                )
                              }
                            />
                          </div>
                        ) : gstTreatment === "cgst_utgst" ? (
                          <>
                            <div className="col-span-1">
                              <Input
                                type="number"
                                placeholder="9"
                                value={l.cgst || ""}
                                onChange={(e) =>
                                  setLines((prev) =>
                                    prev.map((line, i) =>
                                      i === idx
                                        ? {
                                          ...line,
                                          cgst:
                                            parseFloat(e.target.value) || 0,
                                          igst: 0,
                                          sgst: 0,
                                        }
                                        : line
                                    )
                                  )
                                }
                              />
                            </div>
                            <div className="col-span-1">
                              <Input
                                type="number"
                                placeholder="9"
                                value={l.utgst || ""}
                                onChange={(e) =>
                                  setLines((prev) =>
                                    prev.map((line, i) =>
                                      i === idx
                                        ? {
                                          ...line,
                                          utgst:
                                            parseFloat(e.target.value) || 0,
                                          igst: 0,
                                          sgst: 0,
                                        }
                                        : line
                                    )
                                  )
                                }
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="col-span-1">
                              <Input
                                type="number"
                                placeholder="9"
                                value={l.cgst || ""}
                                onChange={(e) =>
                                  setLines((prev) =>
                                    prev.map((line, i) =>
                                      i === idx
                                        ? {
                                          ...line,
                                          cgst:
                                            parseFloat(e.target.value) || 0,
                                          igst: 0,
                                          utgst: 0,
                                        }
                                        : line
                                    )
                                  )
                                }
                              />
                            </div>
                            <div className="col-span-1">
                              <Input
                                type="number"
                                placeholder="9"
                                value={l.sgst || ""}
                                onChange={(e) =>
                                  setLines((prev) =>
                                    prev.map((line, i) =>
                                      i === idx
                                        ? {
                                          ...line,
                                          sgst:
                                            parseFloat(e.target.value) || 0,
                                          igst: 0,
                                          utgst: 0,
                                        }
                                        : line
                                    )
                                  )
                                }
                              />
                            </div>
                          </>
                        )}

                        <div className="col-span-1 text-right font-medium">
                          {formatCurrencyINR(total)}
                        </div>
                        <div className="col-span-1 text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeLine(idx);
                            }}
                            disabled={lines.length === 1}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  <Button variant="secondary" size="sm" onClick={addLine}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Asset
                  </Button>
                </div>

                {/* Enhanced Order Summary */}
                <div className="space-y-4">
                  <Separator />
                  <div className="bg-gradient-to-r from-[hsl(var(--color-admin-primary))/0.05] to-[hsl(var(--color-admin-secondary))/0.05] p-6 rounded-lg border border-[hsl(var(--color-admin-primary))/0.2] space-y-4">
                    <h3 className="font-semibold text-lg text-foreground">
                      Order Summary
                    </h3>
                    <div className="space-y-3">
                      {/* Sub Total */}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Sub Total:
                        </span>
                        <span className="font-medium">
                          {formatCurrencyINR(
                            lines.reduce(
                              (sum, l) => sum + l.unitPrice * l.qty,
                              0
                            )
                          )}
                        </span>
                      </div>

                      {/* GST Breakdown */}
                      {(() => {
                        // helper to build map percentage -> amount for a tax key
                        const buildMap = (key: "cgst" | "sgst" | "igst" | "utgst") =>
                          lines.reduce((map: Record<number, number>, l) => {
                            const pct = Number(l[key] || 0);
                            if (pct <= 0) return map;
                            const amt = (l.unitPrice * l.qty * (l[key] || 0)) / 100;
                            map[pct] = (map[pct] || 0) + amt;
                            return map;
                          }, {});

                        if (gstTreatment === "igst") {
                          const igstMap = buildMap("igst");
                          return (
                            <>
                              {Object.keys(igstMap).map((k) => (
                                <div key={k} className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">IGST ({k}%) :</span>
                                  <span className="font-medium">{formatCurrencyINR(igstMap[Number(k)])}</span>
                                </div>
                              ))}
                            </>
                          );
                        }

                        if (gstTreatment === "cgst_utgst") {
                          // group by pair cgst_utgst
                          const pairMap = lines.reduce((map: Record<string, number>, l) => {
                            const cg = Number(l.cgst || 0);
                            const ut = Number(l.utgst || 0);
                            if (cg <= 0 && ut <= 0) return map;
                            const key = `${cg}_${ut}`;
                            const amt = (l.unitPrice * l.qty * ((cg || 0) + (ut || 0))) / 100;
                            map[key] = (map[key] || 0) + amt;
                            return map;
                          }, {});

                          return (
                            <>
                              {Object.entries(pairMap).map(([pair, amt]) => {
                                const [cg, ut] = pair.split("_");
                                if (cg === ut && Number(cg) > 0) {
                                  return (
                                    <div key={pair} className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">CGST/UTGST ({cg}%) :</span>
                                      <span className="font-medium">{formatCurrencyINR(amt)}</span>
                                    </div>
                                  );
                                }
                                return (
                                  <div key={pair} className="space-y-1">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">CGST ({cg}%) :</span>
                                      <span className="font-medium">{formatCurrencyINR((amt * Number(cg || 0)) / (Number(cg || 0) + Number(ut || 0)) || 0)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">UTGST ({ut}%) :</span>
                                      <span className="font-medium">{formatCurrencyINR((amt * Number(ut || 0)) / (Number(cg || 0) + Number(ut || 0)) || 0)}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          );
                        }

                        // default: cgst + sgst treatment
                        const cgstMap = buildMap("cgst");
                        const sgstMap = buildMap("sgst");

                        // if both have exactly one identical percentage, show combined line(s)
                        const cgKeys = Object.keys(cgstMap).map(Number).sort();
                        const sgKeys = Object.keys(sgstMap).map(Number).sort();
                        if (cgKeys.length === 1 && sgKeys.length === 1 && cgKeys[0] === sgKeys[0]) {
                          const pct = cgKeys[0];
                          const total = (cgstMap[pct] || 0) + (sgstMap[pct] || 0);
                          return (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">CGST/SGST ({pct}%) :</span>
                              <span className="font-medium">{formatCurrencyINR(total)}</span>
                            </div>
                          );
                        }

                        // otherwise list each percentage line separately (CGST then SGST)
                        return (
                          <>
                            {Object.keys(cgstMap).map((k) => (
                              <div key={`cg_${k}`} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">CGST ({k}%) :</span>
                                <span className="font-medium">{formatCurrencyINR(cgstMap[Number(k)])}</span>
                              </div>
                            ))}
                            {Object.keys(sgstMap).map((k) => (
                              <div key={`sg_${k}`} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">SGST ({k}%) :</span>
                                <span className="font-medium">{formatCurrencyINR(sgstMap[Number(k)])}</span>
                              </div>
                            ))}
                          </>
                        );
                      })()}

                      <Separator />
                      <div className="flex justify-between text-lg font-bold text-[hsl(var(--color-admin-primary))]">
                        <span>Total:</span>
                        <span>
                          {formatCurrencyINR(
                            lines.reduce((sum, l) => {
                              const taxPct =
                                gstTreatment === "igst"
                                  ? Number(l.igst) || 0
                                  : (Number(l.cgst) || 0) +
                                    (Number(l.sgst) || 0) +
                                    (Number(l.utgst) || 0);
                              return (
                                sum + l.unitPrice * l.qty * (1 + taxPct / 100)
                              );
                            }, 0)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attachment Section */}
                <div className="space-y-2">
                  <Label>Upload Attachment</Label>
                  <Input
                    type="file"
                    // accept="application/pdf"
                    onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  />
                </div>
              </div>
            </ScrollArea>

            <SheetFooter className="p-6 border-t">
              <Button variant="secondary" onClick={() => setOpen(false)} disabled={isCreating}>
                Cancel
              </Button>
              <Button onClick={create} variant="hero" disabled={isCreating} className="flex items-center gap-2">
                {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                {isCreating ? 'Creating...' : 'Create Purchase Order'}
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
      <MagicUploadDialog
        open={magicUploadOpen}
        onOpenChange={setMagicUploadOpen}
        onExtractedData={handleMagicUploadData}
      />

      <GSTSettingsDialog
        isOpen={gstSettingsOpen}
        onClose={() => setGstSettingsOpen(false)}
        currentTreatment={gstTreatment}
        onTreatmentChange={(treatment) => {
          setGstTreatment(treatment);
          const supplierGstin = customerRequirementType === "sale_lease_back"
            ? (saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]?.gstin) || ""
            : (supplierGstins[selectedGstinIndex] || "");
          // Apply treatment to existing lines
          setLines((prevLines) =>
            prevLines.map((line) =>
              applyGSTRules(line, supplierGstin, treatment)
            )
          );
        }}
        supplierGSTIN={
          customerRequirementType === "sale_lease_back"
            ? (saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]?.gstin) || ""
            : (supplierGstins[selectedGstinIndex] || "")
        }
        supplierAddress={supplierAddresses[selectedGstinIndex]?.formattedAddress || ""}
        supplierStateId={
          customerRequirementType === "sale_lease_back"
            ? (saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]?.state)
            : (supplierAddresses[selectedGstinIndex]?.state)
        }
        indianStates={indiaState}
        ourStateId={12}
        autoDetectedTreatment={
          (() => {
            const supplierStateId = customerRequirementType === "sale_lease_back"
              ? (saleLeaseBackAddressIndex !== null && deliveryAddressList[saleLeaseBackAddressIndex]?.state)
              : (supplierAddresses[selectedGstinIndex]?.state);
            return supplierStateId && indiaState?.length
              ? determineGSTTreatmentFromStateId(supplierStateId, indiaState, 12)
              : undefined;
          })()
        }
      />


      <CustomerAddressManagementDialog
        open={viewAllDeliveryAddressesModalOpen}
        add={addDeliveryAddressModalOpen}
        onOpenChange={handleViewAllDeliveryAddresses}
        addresses={deliveryAddressList || []}
        onUpdate={handleUpdateAddresses}
        indianStates={indiaState}
        customerId={customerId || ""}
      />
    </>
  );
}
